import { toast } from "react-hot-toast";
import { request } from "../../../../utils/api";
import { openDocumentsPrintTab } from "../../../../print/documentPrintService";
import {
  BULK_PRINT_FETCH_CONCURRENCY,
  mapWithConcurrency,
} from "../../../../utils/mapWithConcurrency";
import { getGeneralFormById, isGeneralFormVisible } from "../generalFormsCatalog";
import { buildRouteSheetPrintDocument } from "./routeSheetPrintHtml";
import { buildDailyLogPrintDocument } from "./dailyLogPrintHtml";

/**
 * Registry of print builders keyed by catalog form id.
 * Add a builder when enabling a new form for print.
 */
const PRINT_BUILDERS = {
  "route-sheet": buildRouteSheetPrintDocument,
  "daily-log": buildDailyLogPrintDocument,
};

const RESULT_KEY_BY_FORM = {
  "route-sheet": "routeSheet",
  "daily-log": "dailyLog",
  "patient-consent": "patientConsent",
  medicare: "medicareNonCoverage",
};

function getPrintBranding() {
  try {
    const user = JSON.parse(localStorage.getItem("currentUser") || "{}");
    const logo = user.tenant?.officeLogo || user.officeLogo || "";
    return {
      logoUrl: logo || "/logo.png.png",
      companyName: user.tenant?.companyName || user.companyName || "",
    };
  } catch {
    return { logoUrl: "/logo.png.png", companyName: "" };
  }
}

/**
 * Always fetch by id so print gets inline patientSignatureUrl from S3.
 */
async function resolveFormPayload(row, formDef) {
  if (!row?.id) return null;

  try {
    const response = await request(`/${formDef.apiPath}/${row.id}?includeSignature=true`);
    if (response.status === 1) {
      const key = RESULT_KEY_BY_FORM[formDef.id] || "routeSheet";
      return response.result?.[key] || response.result || row;
    }
  } catch (err) {
    console.error(`Failed to load ${formDef.id} ${row.id} for print`, err);
  }

  // Fallback to list row if GET fails (may still have patientSignatureUrl).
  return row;
}

/**
 * Build print documents for selected General Forms rows and open PrintShell tab
 * (same flow as Application / Documents module).
 *
 * @param {Array<Record<string, unknown>>} rows
 * @param {string} formId - Catalog form id (e.g. "route-sheet")
 * @returns {Promise<boolean>}
 */
export async function printGeneralForms(rows, formId) {
  const formDef = getGeneralFormById(formId);
  const builder = PRINT_BUILDERS[formId];

  if (!formDef || !builder) {
    toast.error("Print is not available for this form yet.");
    return false;
  }
  if (!isGeneralFormVisible(formId)) {
    toast.error("This form is not available.");
    return false;
  }
  if (!rows?.length) {
    toast.error("Select at least one form to print.");
    return false;
  }

  const toastId = toast.loading(
    rows.length === 1
      ? "Preparing form for print…"
      : `Preparing ${rows.length} forms for print…`,
  );

  const branding = getPrintBranding();

  try {
    const results = await mapWithConcurrency(
      rows,
      BULK_PRINT_FETCH_CONCURRENCY,
      async (row) => {
        try {
          const payload = await resolveFormPayload(row, formDef);
          if (!payload) {
            return { ok: false, id: row.id, reason: "Form not found" };
          }
          const doc = builder(payload, branding);
          if (!doc?.html?.trim()) {
            return { ok: false, id: row.id, reason: "Empty print content" };
          }
          return { ok: true, doc };
        } catch (err) {
          console.error(err);
          return {
            ok: false,
            id: row?.id,
            reason: err?.message || "Print failed",
          };
        }
      },
    );

    const documents = [];
    const failed = [];
    results.forEach((result) => {
      if (!result) return;
      if (result.ok && result.doc) documents.push(result.doc);
      else if (!result.ok) failed.push({ id: result.id, reason: result.reason });
    });

    if (!documents.length) {
      toast.error(failed[0]?.reason || "No printable forms in selection.", {
        id: toastId,
      });
      return false;
    }

    const opened = openDocumentsPrintTab(documents, {
      title:
        documents.length === 1
          ? documents[0].title || formDef.title
          : `Print ${documents.length} ${formDef.label} forms`,
      subtitle: "Review the page, then use Print. Close cancels this tab.",
    });

    if (!opened) {
      toast.error("Pop-up blocked. Allow pop-ups to print.", { id: toastId });
      return false;
    }

    if (failed.length) {
      toast.success(
        `Opened print for ${documents.length} form(s). ${failed.length} skipped.`,
        { id: toastId },
      );
    } else {
      toast.success(
        documents.length === 1
          ? "Print preview opened. Use Print in the new tab."
          : `Print preview opened for ${documents.length} forms. Use Print in the new tab.`,
        { id: toastId },
      );
    }
    return true;
  } catch (err) {
    console.error(err);
    toast.error("Failed to prepare print.", { id: toastId });
    return false;
  }
}
